import styled from "@emotion/styled";
import Stack from "@mui/material/Stack";
import propEq from "ramda/src/propEq";
import filter from "ramda/src/filter";
import compose from "ramda/src/compose";
import { useState } from "react";
import PersonIcon from "@atlaskit/icon/glyph/person";
import { Accordion } from "../../UI/Accordion";
import { AccordionDetails } from "../../UI/AccordionDetails";
import { AccordionSummary as UIAccordionSummary } from "../../UI/AccordionSummary";
import { Radio } from "../../UI/Radio";
import { Typography } from "../../UI/Typography";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { IPlacementWorksheetUnitOccupant } from "../../services/PlacementWorksheetApi";
import { usePopupContext } from "../../contexts/PopupContext/context";
import { Box } from "../../UI/Box";
import { IconButton } from "../../UI/IconButton";
import { COLOR_PRIMARY } from "../../constants/styles";
import { URL } from "../../constants/navigation";
import { useRightPopupContext } from "./context";
import { occupantFilterPredicate } from "./PlacementWorksheetUplacedGroupsSection";

interface IProps {
  title: string;
  markColor: string | null;
  residents: IPlacementWorksheetUnitOccupant[];
}

const AccordionSummary = styled(UIAccordionSummary)`
  padding: 0;
  flex-direction: row-reverse;
  min-height: 24px !important;
  .MuiAccordionSummary-content {
    margin: 2px 0 !important;
  }
  & .MuiAccordionSummary-expandIconWrapper {
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    &.Mui-expanded {
      transform: rotate(90deg);
    }
  }
`;

const AccordionSummaryTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "markColor",
})<{ markColor: string | null }>`
  display: flex;
  align-items: center;
  font-weight: 500;
  &::before {
    content: "";
    display: ${(prop) => (prop.markColor ? "inline-block" : "none")};
    width: 4px;
    height: 17px;
    border-radius: ${(prop) => `${(prop.theme.shape.borderRadius as number) * 2}px`};
    background: ${(prop) => prop.markColor};
    margin-right: ${(prop) => prop.theme.spacing(1)};
  }
`;

export const PlacementWorksheetNotMatchedAccordion: React.FC<IProps> = ({ title, markColor, residents }) => {
  const [expanded, setExpanded] = useState(false);
  const { search } = useRightPopupContext();
  const { setSelectedResidents, selectedResidents } = usePopupContext();

  const handleChangeSelectedId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.name;

    const resident = residents.find(propEq("id", id));
    if (!resident) {
      return;
    }

    setSelectedResidents([resident]);
  };

  const handleAccordionChange = () => setExpanded(!expanded);

  const getIsChecked = compose(Boolean, (id: string) => selectedResidents?.find((resident) => resident?.id === id));

  const handleViewProfileClick = (residentId: string) => () => {
    if (!residentId) return;

    window.open(URL.USER.replace(":id", residentId), "_blank");
  };

  const filterResidents = filter(occupantFilterPredicate(search));

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      elevation={0}
      disableGutters
      sx={{
        "&::before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary expandIcon={<ChevronRightRoundedIcon />} aria-controls="panel1d-content" id="panel1d-header">
        <AccordionSummaryTitle markColor={markColor} variant="body1">
          {title}
        </AccordionSummaryTitle>
      </AccordionSummary>
      <AccordionDetails>
        <Stack>
          {filterResidents(residents).map((resident) => (
            <Radio
              key={resident.id}
              name={resident.id}
              onChange={handleChangeSelectedId}
              isChecked={getIsChecked(resident.id)}
              label={
                <Box display="flex" alignItems="center">
                  <Typography variant="body1">
                    {resident.firstName} {resident.lastName}
                  </Typography>
                  {getIsChecked(resident.id) && (
                    <IconButton size="small" sx={{ ml: 0.5 }} onClick={handleViewProfileClick(resident.id)}>
                      <PersonIcon label="person" size="small" primaryColor={COLOR_PRIMARY} />
                    </IconButton>
                  )}
                </Box>
              }
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
